import React, { useContext, useEffect } from "react";
import { Form, Card, Spin, Button, Space } from "antd";
import { useChannelsPost } from "../useChannelsPost";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import PostDetails, { POST_TYPES } from "./ChannelFormComponents/PostDetails";
import Attachments from "./ChannelFormComponents/Attachments";
import TargetBatches from "./ChannelFormComponents/TargetBatches";
import { UserInfoContext } from "Context/CommonContext";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";

interface CreatePostFormProps {
  form: any;
  handleCancel: () => void;
  onSaveSuccess?: (post: any) => void;
  initialValues: any;
  isUpdating?: boolean;
  hasFullAccess?: boolean;
  handlePublish: () => void;
  batchId?: number;
  batchInfo?: any;
  initialBatchOptions?: any[];
  onDeleteAttachment?: (attachmentId: string) => void;
  activeTab?: string;
  selectedSubject?: string;
  batchDetailsData?: any;
}

const CreatePostForm: React.FC<CreatePostFormProps> = ({
  form,
  handleCancel,
  onSaveSuccess,
  initialValues,
  isUpdating,
  hasFullAccess,
  handlePublish,
  batchId,
  batchInfo,
  initialBatchOptions,
  onDeleteAttachment,
  activeTab,
  selectedSubject,
  batchDetailsData,
}) => {
  const userInfo = useContext<any>(UserInfoContext);
  const fexRole = userInfo.fexRole;
  useEffect(() => {
    const key = fexRole == "Faculty" ? "myBatchesIds" : "batchIds";
    if (initialValues) {
      if (initialValues.id) {
        form.setFieldsValue({
          ...initialValues,
          [key]: initialValues["batchIds"] || [],
        });
      } else {
        form.setFieldsValue({
          ...initialValues,
          postType: activeTab,
          subject: selectedSubject,
          [key]: [batchInfo?.value],
        });
      }
    } else {
      form.setFieldsValue({
        postType: activeTab,
        subject: selectedSubject,
        [key]: [batchInfo?.value],
      });
    }
  }, [initialValues, activeTab, selectedSubject, batchInfo, form]);

  const { createPostWithAttachments, isSavingPost } = useChannelsPost(form, {
    onSuccess: (savedPost: any) => {
      if (onSaveSuccess) {
        onSaveSuccess(savedPost);
        form.setFieldsValue({ Image: [], Ebook: [] });
      } else {
        handleCancel();
      }
    },
    selectedPostForUpdate: initialValues,
  });
  const FIELDS_CHANGE = [
    "title",
    "description",
    "Ebook",
    "Image",
    "batchIds",
    "commentAllowed",
    "commentVisibility",
  ];

  const isChangedField = (formValues: any, existingData: any) => {
    return FIELDS_CHANGE.some((field) => {
      const firstValue = formValues[field];
      const secondValue = existingData[field];
      if (Array.isArray(firstValue) || Array.isArray(secondValue)) {
        return (
          JSON.stringify(firstValue ?? []) !== JSON.stringify(secondValue ?? [])
        );
      }
      return firstValue !== secondValue;
    });
  };
  const handleFormFinish = (values: any) => {
    const { Image, videos, Ebook, batchIds, myBatchesIds, ...rest } = values;
    const filesToUpload = [
      ...(Image || []),
      ...(videos || []),
      ...(Ebook || []),
    ];

    const formData = {
      ...rest,
      batchIds: [...(batchIds || []), ...(myBatchesIds || [])],
      postType: activeTab,
      subject: selectedSubject,
      batchId,
    };
    const payload = {
      formData,
      attachments: filesToUpload,
    };

    pushToDataLayerForGTM({
      event: "ag_channels_draft_communication_created",
      BATCH_ID: batchDetailsData?.batchName,
      TAB_NAME: activeTab,
      EMPLOYEE_ID: userInfo?.employeeId,
      USER_ROLE: userInfo?.fexRole,
      SUBJECT: selectedSubject,
      STREAM: batchDetailsData?.academicGroup,
      TITLE: values?.title,
      CONTENT: values?.description,
      ATTACHMENTS: filesToUpload?.length || 0,
      TARGET_BATCHES: batchIds?.length || 0,
      IS_POST: String(activeTab === "POST"),
      VISIBILITY_TOGGLE: String(values?.commentVisibility),
    });

    createPostWithAttachments(BASE_URL + "post/generatePresignedUrl", payload);
  };

  const extraActions = () => {
    return (
      <Space>
        <Button onClick={handleCancel} disabled={isSavingPost}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={isUpdating || isSavingPost}
          disabled={!hasFullAccess}
          onClick={() => {
            form
              .validateFields()
              .then((values: any) => {
                handleFormFinish(values);
              })
              .catch((errorInfo: any) => {
                console.log("Validation Failed:", errorInfo);
              });
          }}
        >
          {initialValues?.id ? "Save" : "Save as Draft"}
        </Button>
        <Button
          type="primary"
          loading={isUpdating || isSavingPost}
          disabled={
            isSavingPost || !hasFullAccess || (initialValues?.id ? false : true)
          }
          onClick={() => {
            form
              .validateFields()
              .then((values: any) => {
                const formValues = { ...values };
                if (!formValues?.Image?.length) {
                  formValues.Image = undefined;
                }
                if (!formValues?.Ebook?.length) {
                  formValues.Ebook = undefined;
                }
                if (isChangedField(formValues, initialValues)) {
                  handleFormFinish(formValues);
                  handlePublish();
                } else {
                  handlePublish();
                }
              })
              .catch((errorInfo: any) => {
                console.log("Validation Failed:", errorInfo);
              });
          }}
        >
          Publish
        </Button>
      </Space>
    );
  };
  const newPostType =
    POST_TYPES.find((pt) => pt.value === activeTab)?.label ||
    initialValues?.postType ||
    "Post";
  return (
    <Form form={form} layout="vertical">
      <Card
        title={
          initialValues?.id
            ? "Edit " + newPostType
            : "Create New " + newPostType
        }
        extra={extraActions()}
      >
        <Spin size="large" spinning={isUpdating || isSavingPost}>
          <PostDetails form={form} />
          <Attachments
            form={form}
            selectedPost={initialValues}
            hasFullAccess={hasFullAccess}
            onDeleteAttachment={onDeleteAttachment}
          />
          <TargetBatches
            form={form}
            selectedPost={initialValues}
            batchInfo={batchInfo}
            initialBatchOptions={initialBatchOptions}
          />
        </Spin>
      </Card>
    </Form>
  );
};

export default CreatePostForm;
